import { useEffect, useState } from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { Layout } from './components/layout';
import { restoreSession } from './lib/api';
import { useAuthStore } from './lib/auth-store';
import { LoginPage } from './pages/login';
import { DashboardPage } from './pages/dashboard';
import { MembersPage } from './pages/members';
import { MemberDetailPage } from './pages/member-detail';
import { KycReviewPage } from './pages/kyc-review';
import { PaymentsPage } from './pages/payments';
import { WalletTopupsPage } from './pages/wallet-topups';
import { PeriodsPage } from './pages/periods';
import { PayoutsPage } from './pages/payouts';
import { PayoutDeductionsPage } from './pages/payout-deductions';
import { EcomPaymentMethodsPage } from './pages/ecom-payment-methods';
import { PosPaymentMethodsPage } from './pages/pos-payment-methods';
import { SettingsPage } from './pages/settings';
import { AuditPage } from './pages/audit';
import { ProductsPage, VirtualProductsPage } from './pages/products';
import { CategoriesPage } from './pages/categories';
import { VariationTypesPage } from './pages/variation-types';
import { SuppliersPage } from './pages/suppliers';
import { PurchaseOrdersPage, CreatePurchaseOrderPage } from './pages/purchase-orders';
import { PurchaseOrderDetailPage } from './pages/purchase-order-detail';
import { CouponsPage } from './pages/coupons';
import { FlashSalePage } from './pages/flash-sale';
import { LandingPackagesPage } from './pages/landing-packages';
import { FeaturedPackagesPage } from './pages/featured-packages';
import { StorefrontPage } from './pages/storefront';
import { LayoutThemePage } from './pages/layout-theme';
import { ImportLegacyDataPage } from './pages/import-legacy-data';
import { OfficialFeedbackPage } from './pages/official-feedback';
import { BranchesPage } from './pages/branches';
import { BranchDetailPage } from './pages/branch-detail';
import { BranchBatchPrintPage } from './pages/branch-batch-print';
import { ShippingPage } from './pages/shipping';
import { MembershipsPage } from './pages/memberships';
import { SlotsPage } from './pages/slots';
import { SlotDetailPage } from './pages/slot-detail';
import { AdminOrdersPage } from './pages/orders';
import { MerchantsPage } from './pages/merchants';
import { AdminMessagesPage } from './pages/messages';
import { FeedbackPage } from './pages/feedback';
import { CompPlanPage } from './pages/compplan';
import { AccessPage } from './pages/access';
import { DistributePage } from './pages/distribute';
import { DistributionRunsPage } from './pages/distribution-runs';
import { DistributionsPage } from './pages/distributions';
import { DlqPage } from './pages/dlq';
import { ReportsPage } from './pages/reports';
import { StockistReceivablesPage } from './pages/stockist-receivables';
import { VortexPage } from './pages/vortex';
import { TrainingVideosPage } from './pages/training-videos';
import { SpecialLinksPage } from './pages/special-links';
import { AnnouncementsPage } from './pages/announcements';
import { ReplicatedPagePage } from './pages/replicated-page';
import { AccountingOverviewPage } from './pages/accounting/overview';
import { ChartOfAccountsPage } from './pages/accounting/accounts';
import { JournalPage } from './pages/accounting/journal';
import { StatementsPage } from './pages/accounting/statements';
import { SupplierInvoicesPage } from './pages/accounting/ap';
import { CostingPage } from './pages/accounting/costing';
import { BillingStatementPage } from './pages/accounting/billing';
import { RequireAccounting } from './pages/accounting/_shared';
import { RequirePerm } from './components/require-perm';
import { RequireSuperAdmin } from './components/require-super-admin';
import { RequirePasswordChange } from './components/require-password-change';
import { ChangePasswordPage } from './pages/change-password';

/**
 * On every mount (including page refresh), try to silently re-hydrate the
 * session from the refresh cookie. The auth store is in-memory only, so a
 * reload starts with `token=null`; without this boot step, `RequireAuth`
 * would always redirect to /login even for users whose refresh cookie is
 * still valid.
 */
function useAuthBoot(): 'pending' | 'ready' {
  const [status, setStatus] = useState<'pending' | 'ready'>('pending');
  useEffect(() => {
    let cancelled = false;
    void restoreSession().finally(() => {
      if (!cancelled) setStatus('ready');
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return status;
}

export function App() {
  const bootStatus = useAuthBoot();
  if (bootStatus === 'pending') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground text-sm">
        Restoring session…
      </div>
    );
  }
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route element={<RequireAuth />}>
        {/* QoL Slice 2 — forced change-password screen. Rendered OUTSIDE the
            password-change guard so the user can actually reach it; INSIDE
            RequireAuth so an anonymous visitor still bounces to /login. */}
        <Route path="/change-password" element={<ChangePasswordPage />} />
        <Route element={<RequirePasswordChange />}>
        <Route
          path="/branches/:id/batches/:batchId/print"
          element={
            <RequirePerm perm="view.products.branches" label="Print batch">
              <BranchBatchPrintPage />
            </RequirePerm>
          }
        />
        <Route element={<Layout />}>
          <Route path="/" element={<DashboardPage />} />
          <Route
            path="/members"
            element={
              <RequirePerm perm="view.members.users" label="Users">
                <MembersPage />
              </RequirePerm>
            }
          />
          <Route
            path="/members/:id"
            element={
              <RequirePerm perm="view.members.users" label="Users">
                <MemberDetailPage />
              </RequirePerm>
            }
          />
          <Route
            path="/kyc-review"
            element={
              <RequirePerm perm="view.members.users" label="KYC Review">
                <KycReviewPage />
              </RequirePerm>
            }
          />
          <Route
            path="/messages"
            element={
              <RequirePerm perm="view.messages" label="Messages">
                <AdminMessagesPage />
              </RequirePerm>
            }
          />
          <Route
            path="/feedback"
            element={
              <RequirePerm perm="view.feedback" label="Feedback">
                <FeedbackPage />
              </RequirePerm>
            }
          />
          <Route
            path="/payments"
            element={
              <RequirePerm perm="view.payments" label="Payments">
                <PaymentsPage />
              </RequirePerm>
            }
          />
          <Route
            path="/wallet-topups"
            element={
              <RequirePerm perm="topup.review" label="Wallet Top-Ups">
                <WalletTopupsPage />
              </RequirePerm>
            }
          />
          <Route
            path="/periods"
            element={
              <RequirePerm perm="view.compensation.periods" label="Periods">
                <PeriodsPage />
              </RequirePerm>
            }
          />
          <Route
            path="/payouts"
            element={
              <RequirePerm perm="view.payouts.requests" label="Payouts">
                <PayoutsPage />
              </RequirePerm>
            }
          />
          <Route
            path="/payout-fees"
            element={
              <RequirePerm perm="view.payouts.fees" label="Fees & Tax">
                <PayoutDeductionsPage />
              </RequirePerm>
            }
          />
          <Route
            path="/ecom-payment-methods"
            element={
              <RequirePerm perm="view.settings.tenant" label="Ecom payment methods">
                <EcomPaymentMethodsPage />
              </RequirePerm>
            }
          />
          <Route
            path="/pos-payment-methods"
            element={
              <RequirePerm perm="view.settings.tenant" label="POS payment methods">
                <PosPaymentMethodsPage />
              </RequirePerm>
            }
          />
          <Route
            path="/settings"
            element={
              <RequirePerm perm="view.settings.tenant" label="Tenant settings">
                <SettingsPage />
              </RequirePerm>
            }
          />
          <Route
            path="/audit"
            element={
              <RequirePerm perm="view.settings.audit" label="Audit log">
                <AuditPage />
              </RequirePerm>
            }
          />
          <Route
            path="/products"
            element={
              <RequirePerm perm="view.products.catalog" label="Products">
                <ProductsPage />
              </RequirePerm>
            }
          />
          <Route
            path="/virtual-products"
            element={
              <RequirePerm perm="view.products.catalog" label="Virtual Products">
                <VirtualProductsPage />
              </RequirePerm>
            }
          />
          <Route
            path="/categories"
            element={
              <RequirePerm perm="view.products.catalog" label="Categories">
                <CategoriesPage />
              </RequirePerm>
            }
          />
          <Route
            path="/variation-types"
            element={
              <RequirePerm perm="view.products.catalog" label="Variation Types">
                <VariationTypesPage />
              </RequirePerm>
            }
          />
          <Route
            path="/suppliers"
            element={
              <RequirePerm perm="view.products.suppliers" label="Suppliers">
                <SuppliersPage />
              </RequirePerm>
            }
          />
          <Route
            path="/purchase-orders"
            element={
              <RequirePerm perm="view.products.purchase_orders" label="Purchase Orders">
                <PurchaseOrdersPage />
              </RequirePerm>
            }
          />
          <Route
            path="/purchase-orders/new"
            element={
              <RequirePerm perm="view.products.purchase_orders" label="Create Purchase Order">
                <CreatePurchaseOrderPage />
              </RequirePerm>
            }
          />
          <Route
            path="/purchase-orders/:id"
            element={
              <RequirePerm perm="view.products.purchase_orders" label="Purchase Order">
                <PurchaseOrderDetailPage />
              </RequirePerm>
            }
          />
          <Route
            path="/flash-sale"
            element={
              <RequirePerm perm="view.settings.tenant" label="Flash Sale">
                <FlashSalePage />
              </RequirePerm>
            }
          />
          <Route
            path="/landing-packages"
            element={
              <RequirePerm perm="view.settings.tenant" label="Landing Packages">
                <LandingPackagesPage />
              </RequirePerm>
            }
          />
          <Route
            path="/featured-packages"
            element={
              <RequirePerm perm="view.settings.tenant" label="Featured Packages">
                <FeaturedPackagesPage />
              </RequirePerm>
            }
          />
          <Route
            path="/storefront"
            element={
              <RequirePerm perm="view.settings.tenant" label="Storefront">
                <StorefrontPage />
              </RequirePerm>
            }
          />
          <Route
            path="/layout-theme"
            element={
              <RequireSuperAdmin label="Layout & Theme">
                <LayoutThemePage />
              </RequireSuperAdmin>
            }
          />
          <Route
            path="/official-feedback"
            element={
              <RequirePerm perm="view.products.catalog" label="Official Store Feedback">
                <OfficialFeedbackPage />
              </RequirePerm>
            }
          />
          <Route
            path="/data-import"
            element={
              <RequireSuperAdmin label="Legacy Data Import">
                <ImportLegacyDataPage />
              </RequireSuperAdmin>
            }
          />
          <Route
            path="/coupons"
            element={
              <RequirePerm perm="view.products.coupons" label="Coupons">
                <CouponsPage />
              </RequirePerm>
            }
          />
          <Route
            path="/branches"
            element={
              <RequirePerm perm="view.products.branches" label="Branches">
                <BranchesPage />
              </RequirePerm>
            }
          />
          <Route
            path="/branches/:id"
            element={
              <RequirePerm perm="view.products.branches" label="Branch detail">
                <BranchDetailPage />
              </RequirePerm>
            }
          />
          <Route
            path="/shipping"
            element={
              <RequirePerm perm="view.products.branches" label="Shipping">
                <ShippingPage />
              </RequirePerm>
            }
          />
          <Route
            path="/memberships"
            element={
              <RequirePerm perm="view.members.memberships" label="Memberships">
                <MembershipsPage />
              </RequirePerm>
            }
          />
          <Route
            path="/slots"
            element={
              <RequirePerm perm="view.members.slots" label="Slots">
                <SlotsPage />
              </RequirePerm>
            }
          />
          <Route
            path="/slots/:id"
            element={
              <RequirePerm perm="view.members.slots" label="Slot detail">
                <SlotDetailPage />
              </RequirePerm>
            }
          />
          <Route path="/orders" element={<AdminOrdersPage />} />
          <Route
            path="/merchants"
            element={
              <RequirePerm perm="view.merchants.accounts" label="Merchants">
                <MerchantsPage />
              </RequirePerm>
            }
          />
          <Route
            path="/compplan"
            element={
              <RequirePerm perm="view.compensation.compplan" label="ComPlan configs">
                <CompPlanPage />
              </RequirePerm>
            }
          />
          <Route
            path="/access"
            element={
              <RequirePerm perm="view.members.access" label="Access">
                <AccessPage />
              </RequirePerm>
            }
          />
          <Route
            path="/distribute"
            element={
              <RequirePerm perm="view.compensation.distribute" label="Unilevel">
                <DistributePage />
              </RequirePerm>
            }
          />
          <Route
            path="/distribution-runs"
            element={
              <RequirePerm perm="view.compensation.distribute" label="Distribution Runs">
                <DistributionRunsPage />
              </RequirePerm>
            }
          />
          <Route
            path="/distributions"
            element={
              <RequirePerm perm="view.compensation.distributions" label="Distributions">
                <DistributionsPage />
              </RequirePerm>
            }
          />
          <Route
            path="/dlq"
            element={
              <RequirePerm perm="view.payouts.dlq" label="DLQ">
                <DlqPage />
              </RequirePerm>
            }
          />
          {/* Accounting preview — sample data, gated on the tenant's
              `accounting_enabled` flag (Settings → System). The real module
              adds proper `view.accounting` permissions. */}
          <Route
            path="/accounting"
            element={
              <RequireAccounting>
                <AccountingOverviewPage />
              </RequireAccounting>
            }
          />
          <Route
            path="/accounting/accounts"
            element={
              <RequireAccounting>
                <ChartOfAccountsPage />
              </RequireAccounting>
            }
          />
          <Route
            path="/accounting/journal"
            element={
              <RequireAccounting>
                <JournalPage />
              </RequireAccounting>
            }
          />
          <Route
            path="/accounting/statements"
            element={
              <RequireAccounting>
                <StatementsPage />
              </RequireAccounting>
            }
          />
          <Route
            path="/accounting/ap"
            element={
              <RequireAccounting>
                <SupplierInvoicesPage />
              </RequireAccounting>
            }
          />
          <Route
            path="/accounting/costing"
            element={
              <RequireAccounting>
                <CostingPage />
              </RequireAccounting>
            }
          />
          <Route
            path="/accounting/billing"
            element={
              <RequireAccounting>
                <BillingStatementPage />
              </RequireAccounting>
            }
          />
          <Route
            path="/reports"
            element={
              <RequirePerm perm="view.reports" label="Reports">
                <ReportsPage />
              </RequirePerm>
            }
          />
          <Route
            path="/reports/stockist-receivables"
            element={
              <RequirePerm perm="view.reports" label="Stockist Receivables">
                <StockistReceivablesPage />
              </RequirePerm>
            }
          />
          <Route
            path="/vortex"
            element={
              <RequirePerm perm="view.compensation.vortex" label="Vortex Monitor">
                <VortexPage />
              </RequirePerm>
            }
          />
          <Route
            path="/training-videos"
            element={
              <RequirePerm perm="view.assets.training_videos" label="Training Videos">
                <TrainingVideosPage />
              </RequirePerm>
            }
          />
          <Route
            path="/replicated-page"
            element={
              <RequirePerm perm="view.content.replicated" label="Replicated Pages">
                <ReplicatedPagePage />
              </RequirePerm>
            }
          />
          <Route
            path="/special-links"
            element={
              <RequirePerm perm="view.assets.training_videos" label="Special Links">
                <SpecialLinksPage />
              </RequirePerm>
            }
          />
          <Route
            path="/announcements"
            element={
              <RequirePerm perm="view.announcements" label="Announcements">
                <AnnouncementsPage />
              </RequirePerm>
            }
          />
        </Route>
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function RequireAuth() {
  const token = useAuthStore((s) => s.token);
  if (!token) return <Navigate to="/login" replace />;
  return <Outlet />;
}
